import type { CSSProperties } from "react";
import { METERS_META, MeterIcon } from "./icons";
import type { MeterView } from "./view-model";
import { PixelIcon } from "./PixelIcon";

export interface MetersProps {
  meters: MeterView[];
  meterStyle: "bars" | "numbers";
  meterLabels: "icons" | "names";
}

export function Meters({ meters, meterStyle, meterLabels }: MetersProps) {
  const byId = new Map(meters.map((m) => [m.id, m]));

  return (
    <div className="meters px" id="meters" data-testid="meter-bars">
      {METERS_META.map((meta) => {
        const raw = byId.get(meta.k)?.value ?? 100;
        const p = Math.max(0, Math.min(100, Math.round(raw)));
        const mk = p >= 60 ? "meter-ok" : p >= 25 ? "meter-warn" : "meter-crit";
        const f = Math.ceil(p / 10);
        const isRefilling = Boolean(byId.get(meta.k)?.refilling);
        const stateClass = [
          p < 25 ? "m crit" : p < 60 ? "m low" : "m",
          isRefilling ? "refilling" : "",
        ]
          .filter(Boolean)
          .join(" ");
        const statusDesc = p < 25 ? ", needs care" : p < 60 ? ", getting low" : ", fine";

        return (
          <div
            key={meta.k}
            className={stateClass}
            id={`m_${meta.k}`}
            data-testid={`meter-${meta.k}`}
            data-value={p}
            data-refilling={isRefilling ? "true" : undefined}
            style={{ "--mc": meta.c } as CSSProperties}
            role="img"
            aria-label={`${meta.n} ${p} percent${statusDesc}`}
          >
            <div className="mt">
              {meterLabels === "icons" ? (
                <MeterIcon id={meta.b} />
              ) : (
                <span className="nmx">{meta.n}</span>
              )}
              <span className="mk" id={`mk_${meta.k}`}>
                <PixelIcon name={mk} />
              </span>
            </div>
            <div id={`mv_${meta.k}`}>
              {meterStyle === "bars" ? (
                <div className="mb">
                  {Array.from({ length: 10 }, (_, i) => (
                    <i key={i} className={i < f ? "on" : undefined} />
                  ))}
                </div>
              ) : (
                <div className="mn" data-testid={`meter-value-${meta.k}`}>
                  {p}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
